import React, { useState } from 'react';
import server from '../../../../api/server';
import { X } from 'lucide-react';
import ModernTimeDropdown from '../../../../dashboard-components/layouts/modernDropdown';

const PROVIDERS = ['exotel', 'plivo', 'twilio', 'ozonetel', 'knowlarity', 'convox', 'tatatele'];

export default function ManagePhoneNumberModal({ number, clientID, serviceID, email, onClose, onSaved }) {
  const deleting = Boolean(number);
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [provider, setProvider] = useState('exotel');
  const [managedBy, setManagedBy] = useState('client_id');
  const [alias, setAlias] = useState('');
  const [release, setRelease] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [releaseUncertain, setReleaseUncertain] = useState(false);
  const canRelease = number?.provider === 'plivo' && number?.managed_by === 'anthropod';

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    let submittedPhone = deleting ? number.phone_number : phone.replace(/[\s()\-]/g, '');
    if (!deleting) {
      if (countryCode === '+91') {
        if (/^(?:\+91|91)[0-9]{10}$/.test(submittedPhone)) submittedPhone = submittedPhone.slice(-10);
        if (!/^[0-9]{10}$/.test(submittedPhone)) {
          setError('With +91, enter 10 digits or paste the full +91 number. Use None for toll-free numbers.');
          return;
        }
        submittedPhone = '+91' + submittedPhone;
      } else if (!/^[0-9]{1,15}$/.test(submittedPhone)) {
        setError('With None, enter digits without a country-code prefix');
        return;
      }
    }
    setBusy(true);
    try {
      const response = await server.manage_voice_agent_phone_number({
        client_id: clientID, service_id: serviceID, email, menu: 'phone-numbers',
        action: deleting ? 'delete' : 'add', phone_number: submittedPhone,
        ...(!deleting ? { country_code: countryCode } : {}),
        provider, managed_by: managedBy, alias_name: alias.trim(), release_on_plivo: deleting && release,
      });
      const body = await response.json();
      if (!response.ok) {
        if (release || body.released_on_plivo || body.release_status === 'unconfirmed') {
          setRelease(false);
          setReleaseUncertain(true);
        }
        setError(body.error || 'Unable to save changes');
        return;
      }
      onSaved(body.message);
    } catch (err) {
      if (release) {
        setRelease(false);
        setReleaseUncertain(true);
      }
      setError('The result could not be confirmed. Refresh the list before retrying. If release was selected, check Plivo first.');
    } finally {
      setBusy(false);
    }
  };
  const inputClass = 'w-full border border-gray-200 rounded-lg px-3 py-2 mt-1 text-sm focus:border-indigo-500';
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
      <form onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="manage-number-title" className="bg-white rounded-lg shadow-lg w-full max-w-md text-sm text-gray-700">
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <h2 id="manage-number-title" className="text-lg font-medium text-gray-900">{deleting ? 'Delete Phone Number' : 'Add Phone Number'}</h2>
          <button type="button" aria-label="Close dialog" disabled={busy} onClick={onClose} className="text-gray-500 hover:text-gray-700 disabled:opacity-50"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3" style={{ maxHeight: '70vh', overflowY: 'auto', fontSize: '13px', lineHeight: '1.5' }}>
        {deleting ? <>
          <p>Delete <strong>{number.phone_number}</strong> ({number.provider}) for <strong>{clientID}</strong>?</p>
          <p className="font-medium text-red-600">This action cannot be undone.</p>
          <p className="text-gray-600">This removes the number configuration. Call history will remain.</p>
          {canRelease && <label className="flex items-start gap-2">
            <input type="checkbox" checked={release} disabled={busy || releaseUncertain} onChange={e => setRelease(e.target.checked)} className="mt-1" />
            <span>Also release this number on Plivo</span>
          </label>}
          <p className="text-gray-600">{release ? 'You will lose ownership of this number on Plivo. It may not be recoverable.' : 'The number will remain with the provider. Provider charges may continue.'}</p>
          {releaseUncertain && <p className="text-sm text-red-700">Check the number in Plivo before continuing. Release is disabled in this dialog to prevent an accidental retry.</p>}
        </> : <>
          <div>
            <label htmlFor="phone-number-country-code" className="block mb-1">Country code</label>
            <ModernTimeDropdown id="phone-number-country-code" ariaLabel="Country code" value={countryCode}
              options={[{ value: '+91', label: '+91' }, { value: '', label: 'None' }]}
              onChange={e => setCountryCode(e.target.value)} width="w-full" fontSize="13px" isDisabled={busy} />
          </div>
          <label className="block">Phone number<input autoFocus required value={phone} onChange={e => setPhone(e.target.value)} placeholder={countryCode === '+91' ? '9876543210 or +919876543210' : '18002026603'} className={inputClass} disabled={busy} /></label>
          <p className="text-xs text-gray-500">{countryCode === '+91' ? 'Saved with +91. A pasted +91 or 91 prefix will not be added twice.' : 'No country code will be added. Use this for special or toll-free numbers.'}</p>
          <div>
            <label htmlFor="phone-number-provider" className="block mb-1">Provider</label>
            <ModernTimeDropdown id="phone-number-provider" ariaLabel="Provider" value={provider}
              options={PROVIDERS.map(p => ({ value: p, label: p === 'tatatele' ? 'Tata Tele' : p.charAt(0).toUpperCase() + p.slice(1) }))}
              onChange={e => setProvider(e.target.value)} width="w-full" fontSize="13px" isDisabled={busy} />
          </div>
          <div>
            <label htmlFor="phone-number-managed-by" className="block mb-1">Managed by</label>
            <ModernTimeDropdown id="phone-number-managed-by" ariaLabel="Managed by" value={managedBy}
              options={[{ value: 'client_id', label: 'Client' }, { value: 'anthropod', label: 'Anthropod' }]}
              onChange={e => setManagedBy(e.target.value)} width="w-full" fontSize="13px" isDisabled={busy} />
          </div>
          <label className="block">Alias name (optional)<input value={alias} onChange={e => setAlias(e.target.value)} className={inputClass} disabled={busy} /></label>
          <p className="text-xs text-gray-500">Register an existing number. This does not purchase a number or configure provider credentials.</p>
        </>}
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 px-4 py-3 border-t border-gray-200">
          <button type="button" disabled={busy} onClick={onClose} className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button type="submit" disabled={busy} className={`px-4 py-2 text-sm text-white rounded hover:opacity-90 disabled:opacity-50 ${deleting ? 'bg-red-600' : 'bg-indigo-600'}`}>{busy ? 'Please wait…' : deleting ? 'Delete number' : 'Add number'}</button>
        </div>
      </form>
    </div>
  );
}
